import { uuidv7 } from "uuidv7";
import { LCS_CART_PREFIX, LCS_HASH_PREFIX } from "./../constants";
import { PA_NAME } from "./../types/product-attribute/product-attribute";
import { deepCopy } from "../utils/common";

const mergeBookingAttributeQuantity = (
  oldOne: BookingAttributeDTO,
  newOne: BookingAttributeDTO,
): BookingAttributeDTO | undefined => {
  if (oldOne?.productAttribute.type !== "OPTIONAL") {
    return;
  }
  if (
    !PA_NAME.QUANTITY.includes(
      oldOne?.productAttribute.name as ProdAttrNameQuanity,
    )
  ) {
    return;
  }

  if (!newOne) {
    return;
  }
  return {
    id: newOne.id,
    productAttribute: newOne.productAttribute,
    value: {
      ...oldOne.value,
      quantity: (oldOne.value?.quantity ?? 0) + (newOne.value?.quantity ?? 0),
    },
  };
};

const mergeBookingAgeDTO = (
  oldOne: BookingAttributeDTO,
  newOne: BookingAttributeDTO,
): BookingAttributeDTO | undefined => {
  if (oldOne?.productAttribute.type !== "DEFAULT") {
    return;
  }

  if (!newOne) {
    return;
  }
  return {
    id: newOne.id,
    productAttribute: newOne.productAttribute,
    value: {
      ...oldOne.value,
      ageQuantities: (oldOne.value?.ageQuantities ?? []).map((oldAge) => {
        const newAge = newOne.value?.ageQuantities?.find(
          (x) => x.age === oldAge.age,
        );
        if (!newAge) return oldAge;
        return {
          ...oldAge,
          quantity: (oldAge.quantity ?? 0) + (newAge.quantity ?? 0),
        };
      }),
    },
  };
};

const hash = (pl: BookingState) => {
  if (pl.product?.productType === "PASS") {
    return Date.now().toString();
  }
  const keys: number[] = pl.bookingAttributes
    .filter((b) => b.value && Object.keys(b.value).length)
    .map((x) => x.productAttribute)
    .filter(
      (k) => k?.id && k?.name,
      // && !PA_NAME.QUANTITY.includes(k?.name as ProdAttrNameQuanity)
    )
    .map((k) => k?.id ?? 0);
  keys.sort();

  return JSON.stringify(
    keys.map((prodaId: number) => {
      const bookingAttr = pl.bookingAttributes.find(
        (x) => x.productAttribute.id === prodaId,
      );
      const o = deepCopy(bookingAttr?.value ?? {});
      delete o?.quantity;
      delete o?.ageQuantities;
      return {
        [prodaId]: JSON.stringify(o),
      };
    }),
  );
};
export const useCartLocalStorage = () => {
  return useCartStorage(localStorage);
};
export const useBookingSessionStorage = (prefix?: string) => {
  return useCartStorage(sessionStorage, prefix);
};

export const genOrderNo = () => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hour = date.getHours().toString().padStart(2, "0");
  const minute = date.getMinutes().toString().padStart(2, "0");
  const second = date.getSeconds().toString().padStart(2, "0");
  const millisecond = date.getMilliseconds().toString().padStart(3, "0");
  return `${year}${month}${day}${hour}${minute}${second}${millisecond}`;
};
export const useCartStorage = (storage: Storage, prefix?: string) => {
  const cartPrefix = LCS_CART_PREFIX + (prefix ?? "");
  const hashPrefix = LCS_HASH_PREFIX + (prefix ?? "");

  const save = (
    id: string,
    bookGroupNo: string,
    o: BookingState,
    hashValue: string,
  ) => {
    storage.setItem(
      cartPrefix + id,
      JSON.stringify({
        ...o,
        bookGroupNo: o.bookGroupNo ?? bookGroupNo,
        id,
        updatedAt: Date.now(),
      }),
    );
    storage.setItem(hashPrefix + id, hashValue);
  };

  const findMergableCartItem = (hashValue: string) => {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(hashPrefix)) {
        const value = storage.getItem(key);
        if (value === hashValue) {
          return key.slice(hashPrefix.length);
        }
      }
    }
  };

  const add = (
    bookGroupNo: string,
    pl: BookingState,
    overwrite?: boolean,
    appendOnly?: boolean,
  ) => {
    bookGroupNo = bookGroupNo || genOrderNo();
    // Add item to local storage
    const hashValue = hash(pl);
    if (overwrite && pl?.id) {
      remove(pl?.id);
      save(pl?.id, bookGroupNo, pl, hashValue);
      return;
    }

    const existId = appendOnly ? null : findMergableCartItem(hashValue);
    const id = (existId || uuidv7()).replaceAll("-", "");
    if (existId) {
      console.log("[CART] able to merge");
      try {
        const o: BookingState = JSON.parse(
          storage.getItem(cartPrefix + id) ?? "",
        );
        console.log("[CART] exist", cartPrefix + id, o);
        if (typeof o === "object") {
          // merge số lượng vào chung
          o.bookingAttributes = (o.bookingAttributes ?? []).map((oldOne) => {
            const newOne: BookingAttributeDTO | undefined =
              pl.bookingAttributes.find(
                (y) => y.productAttribute.id === oldOne?.productAttribute.id,
              );
            if (!newOne) return oldOne;
            // if (overwrite) return newOne;
            return (
              mergeBookingAttributeQuantity(oldOne, newOne) ||
              mergeBookingAgeDTO(oldOne, newOne) ||
              oldOne
            );
          });
          console.log("[CART] merge success");
          save(id, bookGroupNo, o, hashValue);
          return;
        }
      } catch (e) {
        console.error(e);
      }
    }
    // console.log("[CART] save new cart item", JSON.stringify(pl));
    save(id, bookGroupNo, pl, hashValue);
  };

  const remove = (id: string) => {
    // Remove item from local storage
    storage.removeItem(cartPrefix + id);
    storage.removeItem(hashPrefix + id);
  };

  const getAll = ({
    bookingIds,
  }: {
    bookingIds?: string[];
  }): BookingState[] => {
    // Get all items from local storage
    const items = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(cartPrefix)) {
        if (
          !bookingIds?.length ||
          bookingIds.includes(key.substring(cartPrefix.length))
        ) {
          items.push(JSON.parse(storage.getItem(key)!));
        }
      }
    }
    return items as BookingState[];
  };
  const get = (productId: number) => {
    return storage.getItem(cartPrefix + productId);
  };

  const getBasicInfo = () => {
    let total = 0;
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(cartPrefix)) {
        total += 1;
      }
    }
    return {
      total,
    };
  };
  return { add, remove, getAll, get, getBasicInfo };
};
